import { useState } from "react";
import cn from "classnames";
import { usePostHog } from "posthog-js/react";
import { X, Loader2 } from "lucide-react";
import { EmitAnalyticsEvent } from "module/analytics/application/EmitAnalyticsEvent";
import { FlyerTypeCard } from "components/FlyerTypeSelect/FlyerTypeCard";
import { flyerTypesOptions } from "components/FlyerTypeSelect/flyerTypeOptions";
import { FlyerTypeData } from "components/FlyerTypeSelect/FlyerTypeCard/FlyerTypeCard";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import { PossibleFlyerType } from "pages/flyer/builder/drawer";
import { DMDocumentFormats, DMDocumentNames } from "shared/constants";
import { ADM_FORMAT } from "campaign-builder/domain/constants";
import { useStore } from "store";

const buttonBase =
  "relative box-border flex h-10 w-[229px] items-center justify-center rounded border border-solid font-bold";

interface FlyerTypeModalProps {
  setFlyerSelectModalOpen: (value: boolean) => void;
  options?: FlyerTypeData[];
  variant?: "primary" | "secondary";
  flyerType: PossibleFlyerType;
  campaignId: string;
  clientName: string;
  userId: string;
  onModalSubmit?: () => void;
  submitEventName: string;
}

export const FlyerTypeModal = ({
  setFlyerSelectModalOpen,
  options,
  variant = "primary",
  flyerType,
  campaignId,
  clientName,
  userId,
  onModalSubmit,
  submitEventName,
}: FlyerTypeModalProps) => {
  const isPrimary = variant === "primary";
  const posthog = usePostHog();
  const postHogTracker = new EmitAnalyticsEvent(posthog);
  const { updateCampaign } = useStore();
  const [newFlyerType, setNewFlyerType] = useState<PossibleFlyerType>(flyerType);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const disabled = newFlyerType === flyerType;
  const showDisabledLook = disabled && !submitting;

  const handleChangeFlyerType = (newFlyerType: PossibleFlyerType) => {
    setNewFlyerType(newFlyerType);
  };

  const handleClose = () => setFlyerSelectModalOpen(false);

  const onSubmit = async () => {
    if (disabled) return;
    setSubmitting(true);
    if (isPrimary) {
      updateCampaign({ flyerType: newFlyerType });
    }
    postHogTracker.run({
      eventName: submitEventName,
      userId,
      clientName,
      device: navigator.userAgent,
      additionalPayload: {
        campaignId,
        postcardFormat: isPrimary
          ? DMDocumentNames[newFlyerType as keyof typeof DMDocumentNames]
          : ADM_FORMAT[newFlyerType as keyof typeof ADM_FORMAT].replace("_", " "),
      },
    });
    await updateCampaignExtraData({
      campaignId,
      flyerType: newFlyerType,
    });
    setSubmitting(false);
    onModalSubmit && onModalSubmit();
    handleClose();
  };

  return (
    <div className="absolute left-0 top-0 z-[999999] flex h-screen w-screen items-center justify-center bg-black/30">
      <div className="box-border flex w-[548px] flex-col rounded-lg border border-solid border-[#E2E2FC] bg-card p-[35px] dark:border-border">
        <div className="flex w-full items-center justify-end">
          <X onClick={handleClose} className="h-6 w-6 cursor-pointer text-foreground" />
        </div>
        <span className="mb-5 text-2xl font-bold text-[#222336] dark:text-foreground">Choose a postcard format</span>
        <div className="flex flex-wrap justify-between">
          {(options || flyerTypesOptions).map((flyerTypeOption: FlyerTypeData) => {
            return (
              <FlyerTypeCard
                key={flyerTypeOption.dimensions}
                flyerTypeData={flyerTypeOption}
                selectedFlyerType={newFlyerType}
                dataCy={`flyerTypeOption-${flyerTypeOption.type}`}
                onClick={() => {
                  handleChangeFlyerType(flyerTypeOption.type as keyof typeof DMDocumentFormats);
                }}
              />
            );
          })}
        </div>
        <span className="mb-[15px] text-sm italic text-[#4F5B94] dark:text-muted-foreground">
          The format affects the total cost of your campaign.{" "}
        </span>
        <div className="flex w-full items-center justify-between">
          <button
            data-cy="flyerTypeModalCancelButton"
            className={cn(buttonBase, "cursor-pointer border-[#E2E2FC] text-foreground dark:border-border")}
            onClick={handleClose}
          >
            Cancel
          </button>
          <button
            data-cy="flyerTypeModalConfirmButton"
            disabled={disabled}
            className={cn(
              buttonBase,
              showDisabledLook
                ? "cursor-default border-[#E4E4EA] bg-[#E4E4EA] text-[#C2C5DD] dark:border-border dark:bg-muted dark:text-muted-foreground"
                : "cursor-pointer border-primary bg-primary text-primary-foreground"
            )}
            onClick={onSubmit}
          >
            Update
            {submitting && (
              <Loader2 className="absolute right-[50px] top-[10px] h-4 w-4 animate-spin text-primary-foreground" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
